import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">&ldquo;{quote}&rdquo;</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

export function StocktonOnTeesCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Stockton-on-Tees Pothole Claims | 22% Estate Roads in RED | Fixtyer"
        description="Stockton-on-Tees earns GREEN DfT spend scorecards, yet 22% of its 673km unclassified road network is in RED condition — more than double the 10% recorded in 2021. See what the council's own transparency report means for your pothole claim."
        canonical="/councils/stockton-on-tees"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'Stockton-on-Tees', url: '/councils/stockton-on-tees' },
        ]}
      />
      <FAQSchema
        faqs={[
          {
            question: 'Stockton-on-Tees has a GREEN spend scorecard — can I still claim?',
            answer: 'Yes. The DfT Spend scorecard is GREEN because the council matches its capital allocation, but the overall rating is AMBER — driven by road condition. Unclassified roads where 22% are in RED condition in 2024, up from 10% in 2021. Section 58 turns on whether the specific defect that damaged your vehicle was reasonably inspected and repaired, not on whether capital spend equals the DfT allocation.',
          },
          {
            question: 'What if my pothole was on a residential or estate road?',
            answer: 'U-roads make up 673km — 78% of Stockton-on-Tees\'s 860km adopted carriageway network. The council\'s own CVI surveys show RED-condition unclassified roads rising from 10% in 2021 to 22% in 2024. Only 50% of the U-road network is surveyed each year, meaning the whole estate network is measured over a two-year cycle — and the council notes half the data used in its condition figures is always one year old.',
          },
          {
            question: 'Does the rise to 22% RED U-roads strengthen my claim?',
            answer: 'It can. Stockton-on-Tees documents a sustained climb in RED-condition estate roads — 13% in 2020, 10% in 2021, 15% in 2022, 20% in 2023 and 22% in 2024. The council itself says unclassified road condition figures are subject to fluctuation, but the five-year trend is upward. On a residential street, that documented deterioration — combined with the two-year survey gap — is relevant to whether the council had reasonable knowledge of the defect.',
          },
          {
            question: 'Why did pothole repairs jump to 18,881 in 2023/24?',
            answer: 'The council\'s transparency report records 18,881 pothole repairs in 2023/24 — more than double the 8,017 filled in 2022/23 and well above the five-year average of roughly 12,787 per year. The report does not attribute a single cause, but a network producing defects at that rate is one where potholes routinely form between scheduled inspections — exactly the scenario where prior reports and photographic evidence decide claims.',
          },
          {
            question: 'Are the A19 and A66 still Stockton-on-Tees Borough Council?',
            answer: 'Not all of them. Stockton\'s transparency report states the council does not maintain trunk roads — those are National Highways\' responsibility. The council\'s reporting page notes the A19 and A66 may require separate contact depending on which section you are on. Check which authority maintains the road where your incident occurred before claiming.',
          },
          {
            question: 'Should I report the pothole to Stockton before claiming?',
            answer: 'Yes. Reporting creates a dated council record — useful if the defect was reported before your incident, or if the council failed to repair it within a reasonable time. Use the council\'s online damaged road or path form, or call 01642 391959 during office hours for urgent safety risks. Keep your reference number and any confirmation emails.',
          },
          {
            question: 'Does the two-year U-road survey cycle affect Section 58?',
            answer: 'It can. Stockton surveys 50% of its unclassified network each year, completing a full cycle over two years. The council explicitly states that "50% of the data used for calculating unclassified road condition figures is always 1 year old." For Section 58, that raises questions about how current the council\'s knowledge was of your specific residential road at the time of your incident.',
          },
        ]}
      />
      <ServiceSchema
        councilName="Stockton-on-Tees Borough Council"
        councilSlug="stockton-on-tees"
        rating="amber"
      />
      <SpeakableSchema
        url="/councils/stockton-on-tees"
        headline="Stockton-on-Tees Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        <div
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`,
          }}
        />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">Stockton-on-Tees</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="amber" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Best practice</span>
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            Stockton-on-Tees: estate roads in RED condition have more than doubled since 2021
          </h1>

          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            Stockton-on-Tees Borough Council matches its DfT capital allocation pound for pound and earns a{' '}
            <strong className="text-emerald-400">GREEN</strong> spend scorecard. Yet the overall DfT rating is{' '}
            <strong className="text-amber-400">AMBER</strong> — because{' '}
            <strong className="text-rose-400">22% of its 673km unclassified road network</strong> is now in RED
            condition, up from just 10% in 2021, while classified main roads stay below 2% RED.
          </p>

          <div className="bg-amber-500/20 border border-amber-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-amber-400">22%</div>
            <div className="text-white font-medium mt-2">Unclassified roads in RED condition (2024)</div>
            <div className="text-brand-300 text-sm mt-1">
              Up from 10% in 2021 on a 673km estate network that makes up 78% of the borough&apos;s 860km of adopted
              roads — roughly 148km of residential and local routes the council&apos;s own surveys say need maintenance.
            </div>
          </div>
        </div>

        <div
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`,
          }}
        />
      </section>

      {/* The condition picture */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What the condition data shows
          </h2>
          <p className="text-surface-600 mb-8">
            Five years of SCANNER and CVI survey data from Stockton-on-Tees&apos;s own transparency report — main
            roads steady, estate roads deteriorating
          </p>

          {/* A-roads */}
          <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">A-roads (73km — 8.5% of network): stable and green</h3>
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-rose-600">1%</div>
                <div className="text-sm text-rose-800">RED (2024)</div>
                <div className="text-xs text-surface-500">down from 2% in 2020</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-amber-600">12%</div>
                <div className="text-sm text-amber-800">Amber</div>
                <div className="text-xs text-surface-500">broadly flat since 2020</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-emerald-600">87%</div>
                <div className="text-sm text-emerald-800">Green</div>
                <div className="text-xs text-surface-500">up from 82% in 2020</div>
              </div>
            </div>
            <p className="text-surface-600 text-sm">
              All A-roads in the borough are subject to an annual SCANNER condition survey. Credit where due: main
              routes are in genuinely good shape. But A-roads are less than one-tenth of the network.
            </p>
          </div>

          {/* B/C roads */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-amber-900 mb-4">B and C roads (114km — 13% of network): amber creeping up</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-amber-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-amber-100 border-b border-amber-200">
                    <th className="px-4 py-2 text-left font-semibold text-amber-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">3%</td>
                    <td className="px-4 py-2 text-center">24%</td>
                    <td className="px-4 py-2 text-center">73%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center">1%</td>
                    <td className="px-4 py-2 text-center">21%</td>
                    <td className="px-4 py-2 text-center">78%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">1%</td>
                    <td className="px-4 py-2 text-center">11%</td>
                    <td className="px-4 py-2 text-center">88%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">1%</td>
                    <td className="px-4 py-2 text-center">13%</td>
                    <td className="px-4 py-2 text-center">86%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">1%</td>
                    <td className="px-4 py-2 text-center font-bold text-amber-600">17%</td>
                    <td className="px-4 py-2 text-center font-bold">82%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-amber-800 font-medium">
              RED rates on B and C roads have stayed at or below 1% since 2021, but the amber share has climbed back
              to 17% — up from a low of 11% in 2022. Nearly one in five classified local roads may soon need
              maintenance.
            </p>
          </div>

          {/* Spend context */}
          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-2xl font-bold mb-4">GREEN spend — but no overspend cushion</h3>
            <div className="grid sm:grid-cols-3 gap-6 mb-6">
              <div>
                <div className="text-3xl font-bold text-emerald-400">£4.5m</div>
                <div className="text-brand-300 text-sm">DfT capital allocation 2025/26</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-emerald-400">£4.5m</div>
                <div className="text-brand-300 text-sm">Projected capital spend 2025/26</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-emerald-400">65%</div>
                <div className="text-brand-300 text-sm">Estimated preventative share</div>
              </div>
            </div>
            <p className="text-xl font-semibold text-white">
              Unlike some neighbouring Tees Valley authorities, Stockton-on-Tees matches its DfT allocation exactly —
              capital spend has equalled the DfT figure every year since 2020/21. The GREEN spend scorecard reflects
              disciplined budgeting, not surplus investment. With estate roads deteriorating on 78% of the network,
              that distinction matters for where maintenance effort actually lands.
            </p>
          </div>
        </div>
      </section>

      {/* The U-road blind spot */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The 673km estate road network
          </h2>
          <p className="text-surface-600 mb-8">
            78% of Stockton-on-Tees&apos;s adopted roads are unclassified — surveyed over a two-year cycle
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">U-roads in RED condition</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">13%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021</td>
                  <td className="px-6 py-4 text-center font-bold text-emerald-600">10%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">15%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">20%</td>
                </tr>
                <tr className="bg-rose-50">
                  <td className="px-6 py-4 font-mono font-bold text-brand-900">2024</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">22%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">The two-year survey gap</h3>
              <p className="text-surface-600 text-sm mb-3">
                Stockton&apos;s report states that 50% of the unclassified road network is surveyed each year — meaning
                the whole estate network is covered over a two-year period using Coarse Visual Inspection (CVI)
                methodology. Unlike A, B and C roads, which receive annual SCANNER surveys, your residential street
                may not have been condition-assessed in the year of your incident.
              </p>
              <p className="text-surface-600 text-sm">
                At 22% RED in 2024, roughly <strong>148km of estate roads</strong> across Thornaby, Yarm,
                Billingham, Norton and surrounding villages are in the category the council defines as &ldquo;should be
                considered for maintenance.&rdquo;
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Council&apos;s own explanation</h3>
              <p className="text-amber-800 text-sm mb-3">
                Stockton acknowledges that &ldquo;the unclassified road network figures are subject to
                fluctuation&rdquo; and attributes this partly to the two-year survey cycle and partly to the age and
                mixed construction of estate roads.
              </p>
              <p className="text-amber-800 text-sm">
                The council also notes that &ldquo;a lot of the roads are old and made up of layers of differing types
                of road construction materials, leading to the risk of unpredictable rates of deterioration.&rdquo;
                That is documented awareness of elevated deterioration risk on exactly the roads where most pothole
                claims arise.
              </p>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">Why this matters for Section 58</h3>
            <p className="text-brand-200 mb-4">
              To rely on the Section 58 defence, a council must show it had a reasonable system for knowing the
              condition of its roads. For Stockton&apos;s unclassified network, ask:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• When was your road last CVI-surveyed — year one or year two of the cycle?</li>
              <li>• If 22% of U-roads were RED at the last survey, what was done about yours?</li>
              <li>• How does the council track deterioration on roads it inspects only every other year?</li>
              <li>• Did reactive pothole filling on your street address the underlying carriageway condition?</li>
            </ul>
            <p className="text-white font-medium">
              A council cannot claim detailed knowledge of a network where half the condition data is always a year
              old — while RED rates on that network have more than doubled since 2021.
            </p>
          </div>
        </div>
      </section>

      {/* Pothole repair conveyor */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            63,935 pothole repairs in five years
          </h2>
          <p className="text-surface-600 mb-8">
            The scale of reactive repair tells you how many defects this network produces
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Potholes filled</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Daily average</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020/21</td>
                  <td className="px-6 py-4 text-center">12,438</td>
                  <td className="px-6 py-4 text-center text-surface-500">~34/day</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center">9,129</td>
                  <td className="px-6 py-4 text-center text-surface-500">~25/day</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center">8,017</td>
                  <td className="px-6 py-4 text-center text-surface-500">~22/day</td>
                </tr>
                <tr className="border-b border-surface-100 bg-rose-50">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">18,881</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">~52/day</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center">15,470</td>
                  <td className="px-6 py-4 text-center text-surface-500">~42/day</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">Five-year total</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">63,935</td>
                  <td className="px-6 py-4 text-center font-bold text-surface-500">~35/day avg</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">The 2023/24 spike</h3>
              <p className="text-surface-600 text-sm">
                Pothole repairs more than doubled from 8,017 in 2022/23 to 18,881 in 2023/24 — roughly 52 defects
                patched every day for a year. For 2025/26 the council estimates approximately 12,800 pothole repairs.
                A network producing defects at this rate is, by definition, one where potholes routinely form between
                inspections.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Resurfacing vs reactive</h3>
              <p className="text-amber-800 text-sm">
                Carriageway resurfacing fell to just 4.4km (2.7 miles) in 2022/23 — the lowest in five years — before
                recovering to 6.2km in 2024/25. The council plans 6.3km (3.9 miles) for 2025/26. Reactive pothole
                filling and preventative resurfacing tell different stories about whether underlying road condition is
                actually improving.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Council's own words */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What the council says about estate road condition
          </h2>
          <p className="text-surface-600 mb-8">
            Stockton-on-Tees&apos;s own explanation for fluctuating U-road figures — in its own words
          </p>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="As can be seen from the tables, the condition of the classified road network is being maintained at a reasonably steady state, though the unclassified road network figures are subject to fluctuation. This can be attributed to the following reasons: a 2-year survey cycle means that 50% of the data used for calculating unclassified road condition figures is always 1 year old; the unclassified road network is a large network and, due to its nature, a lot of the roads are old and made up of layers of differing types of road construction materials, leading to the risk of unpredictable rates of deterioration."
              source="Stockton-on-Tees Borough Council Local Highways Maintenance Transparency Report (27 June 2025)"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">What this admission means</h3>
              <p className="text-surface-600 text-sm mb-3">
                The council formally distinguishes between steady classified roads and fluctuating estate roads. It
                documents that older, layered construction on unclassified routes creates &ldquo;unpredictable rates of
                deterioration&rdquo; — documented knowledge of elevated risk on the road type where most claims happen.
              </p>
              <p className="text-surface-600 text-sm">
                That does not mean every claim succeeds. But it frames the question Section 58 asks: was the council&apos;s
                inspection and maintenance system reasonable for a network it knows deteriorates unpredictably?
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Questions worth asking</h3>
              <ul className="text-amber-800 text-sm space-y-2">
                <li>• Was your road on the half of the U-network surveyed in the year of your incident?</li>
                <li>• Did the council increase inspection frequency as RED rates climbed from 10% to 22%?</li>
                <li>• If deterioration was known to be unpredictable, why wasn&apos;t the defect caught?</li>
                <li>• Does a pothole patch on layered, ageing construction address the underlying failure?</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* What AMBER means for claims */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming against an AMBER-rated Tees Valley council
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: Stockton-on-Tees is not failing on spend — here is how that changes your approach
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-surface-50 rounded-2xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-4">What works in the council&apos;s favour</h3>
              <ul className="text-surface-700 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> GREEN spend scorecard — capital spend matches DfT allocation every year since 2020/21
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> A-road condition genuinely strong — 1% RED, 87% green in 2024
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> 61–65% of spend classed as preventative maintenance in recent years
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Documented asset management strategy with annual SCANNER surveys on classified roads
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Street Works Permit Scheme and quarterly utility coordination meetings
                </li>
              </ul>
              <p className="text-surface-500 text-sm mt-4 italic">
                Expect a well-documented Section 58 defence on A-roads and main classified routes. Generic claims will struggle.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-semibold text-amber-900 mb-4">What works in yours</h3>
              <ul className="text-amber-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> AMBER condition — 22% of U-roads in RED, more than double the 2021 figure
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 78% of the network surveyed only over a two-year CVI cycle
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 63,935 pothole repairs in five years — including an 18,881 spike in 2023/24
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> B/C amber share rising to 17% in 2024
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Council admits unpredictable deterioration on ageing estate road construction
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The winning strategy here is specificity</h3>
            <p className="text-brand-200 mb-4">
              Against a council with a GREEN spend scorecard and steady classified-road data, your claim lives or dies on
              the <strong className="text-white">specific defect</strong>:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Prior reports of the same pothole (FixMyStreet, council online forms) — proof of actual notice</li>
              <li>• Photos showing the defect&apos;s size, depth and visible age (weathered edges, previous patching)</li>
              <li>• The road&apos;s class — on a U-road, the two-year survey gap is your strongest structural argument</li>
              <li>• Whether the road appeared on the council&apos;s 2025/26 resurfacing programme (3.9 miles planned)</li>
            </ul>
            <p className="text-white font-medium">
              Mac builds exactly this case: he searches for prior reports, assesses your photo evidence, and cites
              Stockton-on-Tees&apos;s own transparency data where it helps you.
            </p>
          </div>
        </div>
      </section>

      {/* Report a pothole CTA */}
      <section className="py-12 bg-surface-50 border-y border-surface-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-4">
            Report the pothole to Stockton-on-Tees first
          </h2>
          <p className="text-surface-600 mb-6">
            Stockton&apos;s transparency report covers reactive maintenance undertaken in response to inspections and
            public reports. Reporting the defect through the council creates a dated record — useful evidence if the
            pothole was reported before your incident, or if the council failed to repair it within a reasonable time.
          </p>
          <a
            href="https://digital.stockton.gov.uk/damaged-road-or-path"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center px-8 py-4 bg-brand-900 text-white font-semibold rounded-xl hover:bg-brand-800 transition-colors"
          >
            Report a damaged road or path — Stockton Council
            <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
          <p className="text-surface-500 text-sm mt-4">
            For immediate safety risks, call the Care for your Area team on 01642 391959 (Mon–Fri 8.30am–5pm, Sat
            9.30am–12.30pm). Keep your reference number and any confirmation emails.
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a pothole in Stockton-on-Tees?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            A well-run council demands a well-built claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No U-road deterioration argument</li>
                <li>• No prior-report search</li>
                <li>• No two-year survey-gap analysis</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional claim pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ 22% U-road RED condition documented</li>
                <li>✅ Two-year CVI survey gap argued</li>
                <li>✅ 63,935 repairs in five years cited</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to Stockton-on-Tees</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=stockton-on-tees"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order my Stockton-on-Tees claim pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View all council ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently asked questions
          </h2>

          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Stockton-on-Tees has a GREEN spend scorecard — can I still claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Yes. The DfT Spend scorecard is GREEN because the council matches its capital allocation, but the
                overall rating is AMBER — driven by road condition. Unclassified roads where 22% are in RED condition in
                2024, up from 10% in 2021. Section 58 turns on whether the specific defect that damaged your vehicle was
                reasonably inspected and repaired, not on whether capital spend equals the DfT allocation.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                What if my pothole was on a residential or estate road?
              </h3>
              <p className="text-surface-600 text-sm">
                U-roads make up 673km — 78% of Stockton-on-Tees&apos;s 860km adopted carriageway network. The
                council&apos;s own CVI surveys show RED-condition unclassified roads rising from 10% in 2021 to 22% in
                2024. Only 50% of the U-road network is surveyed each year, meaning the whole estate network is measured
                over a two-year cycle.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Does the rise to 22% RED U-roads strengthen my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                It can. Stockton-on-Tees documents a sustained climb in RED-condition estate roads — 13% in 2020, 10%
                in 2021, 15% in 2022, 20% in 2023 and 22% in 2024. The council itself says unclassified road condition
                figures are subject to fluctuation, but the five-year trend is upward. On a residential street, that
                documented deterioration — combined with the two-year survey gap — is relevant to whether the council had
                reasonable knowledge of the defect.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Why did pothole repairs jump to 18,881 in 2023/24?
              </h3>
              <p className="text-surface-600 text-sm">
                The council&apos;s transparency report records 18,881 pothole repairs in 2023/24 — more than double the
                8,017 filled in 2022/23. The report does not attribute a single cause, but a network producing defects at
                that rate is one where potholes routinely form between scheduled inspections — exactly the scenario where
                prior reports and photographic evidence decide claims.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Are the A19 and A66 still Stockton-on-Tees Borough Council?
              </h3>
              <p className="text-surface-600 text-sm">
                Not all of them. Stockton&apos;s transparency report states the council does not maintain trunk roads —
                those are National Highways&apos; responsibility. The council&apos;s reporting page notes the A19 and A66
                may require separate contact depending on which section you are on. Check which authority maintains the
                road where your incident occurred before claiming.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Should I report the pothole to Stockton before claiming?
              </h3>
              <p className="text-surface-600 text-sm">
                Yes. Reporting creates a dated council record — useful if the defect was reported before your incident,
                or if the council failed to repair it within a reasonable time. Use the council&apos;s online damaged road
                or path form, or call 01642 391959 during office hours for urgent safety risks. Keep your reference
                number and any confirmation emails.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Does the two-year U-road survey cycle affect Section 58?
              </h3>
              <p className="text-surface-600 text-sm">
                It can. Stockton surveys 50% of its unclassified network each year, completing a full cycle over two
                years. The council explicitly states that &ldquo;50% of the data used for calculating unclassified road
                condition figures is always 1 year old.&rdquo; For Section 58, that raises questions about how current the
                council&apos;s knowledge was of your specific residential road at the time of your incident.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Attribution footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources:{' '}
            <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a>{' '}
            |{' '}
            <a
              href="https://www.stockton.gov.uk/article/17809/Local-Highways-Maintenance-Transparency-Report"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Stockton-on-Tees Borough Council Local Highways Maintenance Transparency Report (27 June 2025)
            </a>
            . Contains public sector information licensed under the{' '}
            <a
              href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Open Government Licence v3.0
            </a>
            .
          </p>
        </div>
      </section>
    </div>
  );
}
